import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Users, Utensils, Clock, Award, Menu, X, TrendingUp, Heart, ShieldCheck, Leaf, DollarSign, Phone, Mail, MapPin } from 'lucide-react';
import productImg from '../../assets/logo.jpg'
import { CONTACT_ROUTE, DELETE_ACCOUNT_ROUTE, LOGIN_ROUTE, PRIVACY_POLICY_ROUTE, REFUND_POLICY_ROUTE, REGISTER_ROUTE, TERMS_CONDITION_ROUTE } from '../../routes/web/rootRoutes';
import { ADMIN_ROUTE } from '../../routes/admin/adminRoutes';
import LoginCard from './LoginCard';
import { Link } from 'react-router-dom';
import apk from "../../assets/apk.apk"
const MoboEatHomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentReview, setCurrentReview] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const slides = [
    {
      title: "Delicious Corporate Meals",
      subtitle: "Fuel Your Team's Success",
      desc: "Fresh, healthy, and delicious meals delivered to your workplace every day",
      bg: "from-orange-500 via-red-500 to-pink-600",
      image: "🍱"
    },
    {
      title: "Customized Menu Options",
      subtitle: "Every Taste Matters",
      desc: "Diverse cuisines tailored to your team's unique preferences",
      bg: "from-purple-500 via-pink-500 to-red-600",
      image: "🍽️"
    },
    {
      title: "Seamless Canteen Management",
      subtitle: "Smart Food Solutions",
      desc: "Technology-driven service for modern workplaces",
      bg: "from-blue-500 via-cyan-500 to-teal-600",
      image: "⚡"
    },
    {
      title: "Hygiene & Quality First",
      subtitle: "Trust in Every Bite",
      desc: "FSSAI certified kitchens with highest safety standards",
      bg: "from-green-500 via-emerald-500 to-teal-600",
      image: "✨"
    }
  ];

  const reviews = [
    {
      name: "Rajesh Kumar",
      company: "Tech Innovations Pvt Ltd",
      role: "HR Director",
      rating: 5,
      text: "MoboEat has completely transformed our office canteen experience. The food quality is exceptional, the variety is amazing, and the service is always punctual. Our employees are happier and more productive!",
      avatar: "RK",
      color: "from-orange-500 to-red-600"
    },
    {
      name: "Priya Sharma",
      company: "Digital Solutions Inc",
      role: "Operations Manager",
      rating: 5,
      text: "Outstanding service from day one! The team at MoboEat understands corporate needs perfectly. Our employees love the freshness and variety. Highly recommend for any organization looking for quality catering.",
      avatar: "PS",
      color: "from-purple-500 to-pink-600"
    },
    {
      name: "Amit Patel",
      company: "Finance Corp",
      role: "CEO",
      rating: 5,
      text: "Best decision we made for our office this year. The monthly plans are incredibly cost-effective, and the food quality never disappoints. Employee satisfaction has gone up significantly!",
      avatar: "AP",
      color: "from-blue-500 to-cyan-600"
    },
    {
      name: "Sneha Reddy",
      company: "Marketing Hub",
      role: "People & Culture Head",
      rating: 5,
      text: "Amazing experience with MoboEat! They truly care about what they serve. Fresh ingredients, great taste, and excellent service. Our team looks forward to lunch every single day now!",
      avatar: "SR",
      color: "from-green-500 to-emerald-600"
    },
    {
      name: "Vikram Singh",
      company: "Startup Accelerator",
      role: "Founder",
      rating: 5,
      text: "As a fast-growing startup, we needed a reliable food partner. MoboEat scaled with us seamlessly. From 20 to 200 employees, they have maintained the same high quality and service standards.",
      avatar: "VS",
      color: "from-indigo-500 to-purple-600"
    }
  ];

  const features = [
    {
      icon: Utensils,
      title: "Fresh Ingredients",
      desc: "Daily sourced organic produce from trusted suppliers",
      color: "from-orange-400 to-red-500"
    },
    {
      icon: Clock,
      title: "On-Time Delivery",
      desc: "99.9% punctuality rate with real-time tracking",
      color: "from-blue-400 to-cyan-500"
    },
    {
      icon: Users,
      title: "Bulk Orders",
      desc: "Catering for teams from 20 to 5000+ employees",
      color: "from-purple-400 to-pink-500"
    },
    {
      icon: Award,
      title: "Quality Certified",
      desc: "FSSAI approved with ISO 22000 certification",
      color: "from-green-400 to-emerald-500"
    },
    {
      icon: ShieldCheck,
      title: "Food Safety",
      desc: "Stringent hygiene protocols at every step",
      color: "from-red-400 to-pink-500"
    },
    {
      icon: Leaf,
      title: "Healthy Options",
      desc: "Nutritionist-approved balanced meal plans",
      color: "from-teal-400 to-green-500"
    },
    {
      icon: DollarSign,
      title: "Flexible Plans",
      desc: "Customizable packages to fit any budget",
      color: "from-yellow-400 to-orange-500"
    },
    {
      icon: Heart,
      title: "Customer Care",
      desc: "24/7 support team for all your needs",
      color: "from-pink-400 to-red-500"
    }
  ];

  const menuItems = [
    { name: "North Indian", emoji: "🍛", popular: true },
    { name: "South Indian", emoji: "🥘", popular: true },
    { name: "Continental", emoji: "🍝", popular: false },
    { name: "Chinese", emoji: "🍜", popular: true },
    { name: "Healthy Bowls", emoji: "🥗", popular: false },
    { name: "Desserts", emoji: "🍰", popular: false }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-lg z-50 border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex items-center space-x-3 cursor-pointer group">
              <div className="relative">
                <img
                  src={productImg}
                  alt="MoboEat Logo"
                  className="h-20 w-20 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow"
                />
                <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  MoboEat
                </span>
                <p className="text-xs text-gray-500 font-medium">Corporate Canteen Services</p>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4">

              <a href="#home" className="text-gray-700 hover:text-orange-600 font-semibold cursor-pointer transition-colors relative group">
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-600 group-hover:w-full transition-all"></span>
              </a>
              {/* <Link to={ADMIN_ROUTE} className="text-gray-700 hover:text-orange-600 font-semibold cursor-pointer transition-colors relative group">
                Moboeat Admin Login
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-600 group-hover:w-full transition-all"></span>
              </Link> */}

              <Link to={LOGIN_ROUTE} className="px-6 py-2.5 text-orange-600 border-2 border-orange-600 rounded-full font-bold hover:bg-orange-50 transition-all transform hover:scale-105">
                User Login
              </Link>
              <a to={REGISTER_ROUTE} className="px-6 cursor-pointer py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all">
                Sign Up
              </a>
            </div>

            <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={28} className="text-orange-600" /> : <Menu size={28} className="text-orange-600" />}
            </button>
          </div>
        </div>


        {menuOpen && (
          <div className="md:hidden bg-white border-t border-orange-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a href="#home" onClick={() => setMenuOpen(false)} className="block text-gray-700 hover:text-orange-600 font-semibold py-2">Home</a>
              <a href="#services" onClick={() => setMenuOpen(false)} className="block text-gray-700 hover:text-orange-600 font-semibold py-2">Services</a>
              <a href="#menu" onClick={() => setMenuOpen(false)} className="block text-gray-700 hover:text-orange-600 font-semibold py-2">Menu</a>
              <a href="#reviews" onClick={() => setMenuOpen(false)} className="block text-gray-700 hover:text-orange-600 font-semibold py-2">Reviews</a>
              <a href="#contact" onClick={() => setMenuOpen(false)} className="block text-gray-700 hover:text-orange-600 font-semibold py-2">Contact</a>
              <a href="https://moboeat.com/auth/login" className="block w-full px-6 py-3 text-center text-orange-600 border-2 border-orange-600 rounded-full font-bold">Login</a>
              <a href="https://moboeat.com/auth/signup" className="block w-full px-6 py-3 text-center bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full font-bold">Sign Up</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Slider */}
      <div id="home" className="relative h-screen mt-20 overflow-hidden">
        {slides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-all duration-1000 ${idx === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
              }`}
          >
            <div className={`h-full bg-gradient-to-br ${slide.bg} flex items-center justify-center relative overflow-hidden`}>
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-20 left-20 text-9xl animate-bounce">{slide.image}</div>
                <div className="absolute bottom-20 right-20 text-9xl animate-bounce delay-300">{slide.image}</div>
                <div className="absolute top-1/2 left-1/3 text-7xl animate-pulse">{slide.image}</div>
              </div>

              <div className="text-center text-white px-4 max-w-5xl relative z-10">
                <div className="mb-6 inline-block">
                  <span className="text-8xl animate-pulse">{slide.image}</span>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 animate-fade-in leading-tight">
                  {slide.title}
                </h1>
                <p className="text-2xl md:text-4xl mb-4 font-light opacity-95">
                  {slide.subtitle}
                </p>
                <p className="text-lg md:text-xl mb-10 opacity-90 max-w-3xl mx-auto">
                  {slide.desc}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a href="https://moboeat.com/auth/signup" className="px-10 py-5 bg-white text-orange-600 rounded-full font-bold text-lg hover:shadow-2xl transform hover:scale-110 transition-all">
                    Get Started Free
                  </a>
                  <a href="#contact" className="px-10 py-5 bg-white/20 backdrop-blur-md text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/30 transition-all">
                    Connect with us
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={() => setCurrentSlide((currentSlide - 1 + slides.length) % slides.length)}
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md p-4 rounded-full hover:bg-white/40 transition-all transform hover:scale-110"
        >
          <ChevronLeft size={32} className="text-white" />
        </button>
        <button
          onClick={() => setCurrentSlide((currentSlide + 1) % slides.length)}
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md p-4 rounded-full hover:bg-white/40 transition-all transform hover:scale-110"
        >
          <ChevronRight size={32} className="text-white" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`h-3 rounded-full transition-all ${idx === currentSlide ? 'w-12 bg-white shadow-lg' : 'w-3 bg-white/50 hover:bg-white/70'
                }`}
            />
          ))}
        </div>
      </div>



      {/* <LoginCard /> */}

      <div id="services" className="py-24 px-4 bg-[linear-gradient(0deg,#FFFFFF_0%,#FFEDEF_100%)]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl md:text-6xl font-extrabold mt-3 mb-6 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Why Choose MoboEat?
            </h2>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
              Our app is packed with features that enable you to experience food &
              beverage services like never before
            </p>
          </div>

          <div className="h-fit w-full overflow-hidden">
            <div className="relative mx-auto grid aspect-[3/1] w-full grid-cols-6 grid-rows-2 gap-4 md:max-w-screen-md xl:max-w-[1024px] xl:gap-6">
              {/* LEFT GRID */}
              <div className="relative col-span-2 col-start-1 row-span-2 grid h-full grid-cols-2 grid-rows-2 gap-4 xl:gap-6">

                <div className="flex flex-col items-center justify-start pt-2 rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-bottom-right scale-75">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/d0f1639403f80f8f2c19e0d538222e661742455804.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Healthy
                    <br />
                  </div>
                </div>

                <div className="flex flex-col items-center justify-start pt-2 rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-bottom-left scale-75 -translate-y-6">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/82f145180cd6f920a8a8617dda366a0a1742455963.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Veg Mode
                    <br />
                  </div>
                </div>

                <div className="flex flex-col items-center justify-start rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-top-right scale-75 pt-0 translate-x-6">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/5e7aab0f183b36fc12c29279f0cb55181742462245.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Plan
                    <br />
                    a Party
                  </div>
                </div>

                <div className="flex flex-col items-center justify-start pt-2 rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-top-left scale-75 -translate-y-6 translate-x-6">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/867f86a10503998e437963bb37c451591742455764.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Gift Cards
                    <br />
                  </div>
                </div>

              </div>

              {/* CENTER MOBILE VIEW */}
              <div className="relative col-span-2 col-start-3 row-span-2 flex h-full overflow-hidden">
                <div className="h-full w-full flex flex-col items-center justify-end transition-transform duration-700 ease-out translate-y-0">
                  <div className="absolute -bottom-5 left-0 h-full w-full">
                    <img
                      src="https://b.zmtcdn.com/data/o2_assets/3f7e2757e62fd22592b879bd56b666011742294630.png"
                      alt="mobile frame"
                      className="mx-auto h-auto w-[92%]"
                      loading="lazy"
                    />
                  </div>

                  <div className="z-50 mb-6 xl:mb-8 aspect-[8/9] w-1/2 scale-90">
                    <div className="flex w-full flex-col items-center justify-start rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white mx-auto aspect-[26/29] pt-4">
                      <div className="w-full">
                        <img
                          className="mx-auto h-auto w-full"
                          src="https://b.zmtcdn.com/data/o2_assets/cc1caf220c91be38dd94cce12b416fcd1746550226.png"
                          loading="lazy"
                          alt=""
                        />
                      </div>
                      <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                        Schedule
                        <br /> your order
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT GRID */}
              <div className="relative col-span-2 col-start-5 row-span-2 grid h-full -translate-x-6 grid-cols-2 grid-rows-2 gap-4 xl:gap-6">
                <div className="flex flex-col items-center justify-start pt-2 rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-bottom-right scale-75 -translate-x-6">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/6e27c9acde6045c272a28e6eb275727e1742455789.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Gourmet
                    <br />
                  </div>
                </div>

                <div className="flex flex-col items-center justify-start pt-2 rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-bottom-left scale-75 -translate-y-6 -translate-x-6">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/813952c961fd13588cb71867d84ea7dc1742455815.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Offers
                    <br />
                  </div>
                </div>

               <br />

                <div className="flex flex-col items-center justify-start pt-2 rounded-2xl xl:rounded-3xl border border-lavenderMist pb-2 shadow-lg 2xl:rounded-[32px] bg-white aspect-[26/29] w-full origin-top-left scale-75 -translate-y-6">
                  <div className="w-full">
                    <img
                      className="mx-auto h-auto w-full"
                      src="https://b.zmtcdn.com/data/o2_assets/5e973dd10c387878009c66d625ae541a1746550690.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="px-2 text-center text-sm my-auto xl:text-lg xl:leading-[24px] font-normal text-darkSlateGrey md:px-4 justify-self-end">
                    Collections
                    <br />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-14 left-0 h-[112px] w-full bg-[linear-gradient(0deg,_rgba(255,_255,_255,_100)_0%,_rgba(255,_255,_255,_50)_70%,_rgba(255,_255,_255,_0)_100%)]"></div>
            </div>
          </div>




        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-9xl">🍱</div>
          <div className="absolute bottom-10 right-10 text-9xl">⚡</div>
          <div className="absolute top-1/2 left-1/3 text-7xl">✨</div>
        </div>
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8 text-center text-white relative z-10">
          <div className="transform hover:scale-110 transition-all">
            <TrendingUp size={56} className="mx-auto mb-4" />
            <div className="text-6xl font-extrabold mb-2">500+</div>
            <div className="text-xl opacity-90 font-semibold">Corporate Clients</div>
          </div>
          <div className="transform hover:scale-110 transition-all">
            <Users size={56} className="mx-auto mb-4" />
            <div className="text-6xl font-extrabold mb-2">50K+</div>
            <div className="text-xl opacity-90 font-semibold">Daily Meals Served</div>
          </div>
          <div className="transform hover:scale-110 transition-all">
            <Heart size={56} className="mx-auto mb-4" />
            <div className="text-6xl font-extrabold mb-2">98%</div>
            <div className="text-xl opacity-90 font-semibold">Satisfaction Rate</div>
          </div>
          <div className="transform hover:scale-110 transition-all">
            <Award size={56} className="mx-auto mb-4" />
            <div className="text-6xl font-extrabold mb-2">10+</div>
            <div className="text-xl opacity-90 font-semibold">Years Experience</div>
          </div>
        </div>
      </div>

      {/* Menu Section */}
      <div id="menu" className="py-24 px-4 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold text-sm uppercase tracking-wider">Our Offerings</span>
            <h2 className="text-4xl md:text-6xl font-extrabold mt-3 mb-6 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Diverse Menu Options
            </h2>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
              From traditional favorites to international cuisines, we have got something for everyone
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {menuItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all group cursor-pointer relative"
              >
                {item.popular && (
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    Popular
                  </div>
                )}
                <div className="text-6xl mb-4 group-hover:scale-125 transition-transform">{item.emoji}</div>
                <h3 className="font-bold text-gray-800 group-hover:text-orange-600 transition-colors text-center">
                  {item.name}
                </h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="https://moboeat.com/auth/signup" className="inline-block px-10 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all">
              View Full Menu
            </a>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div id="reviews" className="py-24 px-4 bg-gradient-to-br from-white via-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-4xl md:text-6xl font-extrabold mt-3 mb-6 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              What Our Clients Say
            </h2>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
              Trusted by leading companies across India for exceptional food service
            </p>
          </div>

          <div className="relative min-h-[400px]">
            {reviews.map((review, idx) => (
              <div
                key={idx}
                className={`transition-all duration-700 ${idx === currentReview ? 'opacity-100 scale-100 relative' : 'opacity-0 scale-95 absolute inset-0'
                  }`}
              >
                <div className="bg-white p-10 md:p-12 rounded-3xl shadow-2xl max-w-4xl mx-auto border-t-4 border-orange-500">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
                    <div className={`bg-gradient-to-br ${review.color} text-white w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold shadow-lg flex-shrink-0`}>
                      {review.avatar}
                    </div>
                    <div className="text-center md:text-left flex-grow">
                      <h4 className="text-2xl font-bold text-gray-800 mb-1">{review.name}</h4>
                      <p className="text-orange-600 font-semibold mb-1">{review.role}</p>
                      <p className="text-gray-600">{review.company}</p>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} size={28} className="text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <div className="relative">
                    <div className="text-6xl text-orange-200 absolute -top-4 -left-2">"</div>
                    <p className="text-gray-700 text-lg md:text-xl italic leading-relaxed pl-8">
                      {review.text}
                    </p>
                    <div className="text-6xl text-orange-200 absolute -bottom-8 right-0">"</div>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-center mt-12 gap-3">
              {reviews.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentReview(idx)}
                  className={`h-3 rounded-full transition-all ${idx === currentReview ? 'w-12 bg-orange-600 shadow-lg' : 'w-3 bg-gray-300 hover:bg-orange-300'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-4 bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-20 text-9xl animate-bounce">🍱</div>
          <div className="absolute bottom-10 right-20 text-9xl animate-bounce delay-300">⚡</div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
            Ready to Elevate Your Workplace Dining?
          </h2>
          <p className="text-xl md:text-2xl mb-10 opacity-95">
            Join hundreds of companies enjoying delicious, healthy meals every day
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="https://moboeat.com/auth/signup" className="px-12 py-5 bg-white text-orange-600 rounded-full font-bold text-lg hover:shadow-2xl transform hover:scale-110 transition-all">
              Start Your Free Trial
            </a>
            <a href="#contact" className="px-12 py-5 bg-white/20 backdrop-blur-md text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/30 transition-all">
              Contact Sales
            </a>
          </div>
          <p className="mt-8 text-sm opacity-75">No credit card required • 14-day free trial • Cancel anytime</p>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="py-24 px-4 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold text-sm uppercase tracking-wider">Get In Touch</span>
            <h2 className="text-4xl md:text-6xl font-extrabold mt-3 mb-6 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Contact Us Today
            </h2>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
              Have questions? We are here to help you transform your workplace dining experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-400 to-red-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Phone size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800 text-center">Call Us</h3>
              <p className="text-gray-600 text-center mb-2">Mon-Sat: 9 AM - 7 PM</p>
              <a href="tel:+918767381818" className="text-orange-600 font-bold text-lg block text-center hover:text-orange-700">
                +91 8767 3818 18
              </a>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-blue-400 to-cyan-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Mail size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800 text-center">Email Us</h3>
              <p className="text-gray-600 text-center mb-2">We will respond within 24 hours</p>
              <a href="mailto:info@moboeat.com" className="text-orange-600 font-bold text-lg block text-center hover:text-orange-700">
                info@moboeat.com
              </a>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-purple-400 to-pink-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <MapPin size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800 text-center">Visit Us</h3>
              <p className="text-gray-600 text-center mb-2">Corporate Office</p>
              <p className="text-orange-600 font-bold text-center hover:text-orange-700">
                Mumbai , India
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-100 to-red-100 p-8 md:p-12 rounded-3xl text-center">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">Request a Quote</h3>
            <p className="text-gray-700 mb-8 text-lg">
              Fill out the form and our team will get back to you within 24 hours
            </p>
            <a href="https://moboeat.com/auth/signup" className="inline-block px-12 py-5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all">
              Get Free Consultation
            </a>
          </div>
        </div>
      </div>


      <div className="relative mx-auto mb-16 w-[88%]  py-10 xl:mb-40">
        <div
          className="relative rounded-[45px] p-[5px] mx-auto max-w-[1488px] shadow-[0px_8px_10px_0px_#1C1C1C14]"
          style={{ background: "linear-gradient(180deg, #FFEDEF 0%, #FFDBE0 100%)" }}
        >
          <div
            className="rounded-[40px]"
            style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FFEDEF 100%)" }}
          >
            <div className="relative flex size-full w-full items-center justify-evenly gap-7 overflow-hidden px-12 pt-6 lg:pt-8 xl:pt-[60px]">

              {/* LEFT CONTENT */}
              <div className="flex flex-col items-center">
                <div>
                  <div>
                    <div className="mb-2 text-start text-xl font-bold text-zCoal md:text-2xl lg:mb-4 lg:text-3xl xl:text-[40px]">
                      Download the app now!
                    </div>

                    <div className="text-start text-sm font-normal text-comet md:text-base lg:text-xl xl:text-2xl">
                      Experience seamless online ordering <br /> only on the MoboEat app
                    </div>
                  </div>

                  {/* STORE BUTTONS */}
                  <div className="z-50 mt-4 flex max-h-[48px] w-4/5 items-center justify-items-start gap-4 lg:mt-10 xl:max-h-[72px] 2xl:gap-[28px]">
                    <a
                      href={apk}
                      target="_blank"
                      rel="noreferrer"
                      className="z-50 size-fit transition-transform duration-200 hover:scale-110"
                    >
                      <div>
                        <img
                          alt="App Download Logo"
                          className="h-12 object-contain md:h-[48px] xl:h-[56px] 2xl:h-[64px]"
                          src="https://b.zmtcdn.com/data/o2_assets/aad864bd17860b27634fe621001c32db1739350431.png"
                          loading="lazy"
                        />
                      </div>
                    </a>

                    <a
                      href={apk}
                      target="_blank"
                      rel="noreferrer"
                      className="z-50 size-fit transition-transform duration-200 hover:scale-110"
                    >
                      <div>
                        <img
                          alt="App Download Logo"
                          className="h-12 object-contain md:h-[48px] xl:h-[56px] 2xl:h-[64px]"
                          src="https://b.zmtcdn.com/data/o2_assets/df6464de32f4a09262cee301f65aaa661739351256.png"
                          loading="lazy"
                        />
                      </div>
                    </a>
                  </div>
                </div>
              </div>

              {/* MOBILE MOCKUP + QR */}
              <div className="relative z-50 w-1/2 lg:max-w-[348px] xl:max-w-[454px] transition-transform duration-700 ease-out translate-y-0">
                <div className="size-full">
                  <img
                    src="https://b.zmtcdn.com/data/o2_assets/3f7e2757e62fd22592b879bd56b666011742294630.png"
                    loading="lazy"
                    alt=""
                  />
                </div>

                <div className="absolute left-0 top-0 flex size-full flex-col items-center justify-end gap-5">
                  <div className="w-[70%] text-center text-xs font-medium text-comet md:text-sm lg:w-3/5 lg:text-base xl:text-xl">
                    Scan the QR code to download the app
                  </div>

                  <div className="mb-3 flex items-center justify-center xl:mb-12">
                    <img
                      src="https://github.com/raviranjan81/gsap/blob/main/Screenshot%202025-12-10%20011143.png?raw=true"
                      loading="lazy"
                      alt="download zomato app"
                      className="aspect-square w-1/2"
                    />
                  </div>
                </div>
              </div>

              {/* BACKGROUND DECORATIVE CIRCLES */}
              <div className="absolute right-[-30%] top-[30%] aspect-square w-[90%]">
                <div className="flex aspect-square w-full items-center justify-center rounded-full border-2 border-red-100">
                  <div className="flex aspect-square w-4/5 items-center justify-center rounded-full border-2 border-red-100">
                    <div className="flex aspect-square w-3/4 items-center justify-center rounded-full border-2 border-red-100"></div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>

      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://moboeat.com/static/media/logo.7ee9e620560c0c86c1fa.jpg"
                  alt="Logo"
                  className="h-12 w-12 rounded-lg shadow-lg"
                />
                <span className="text-2xl font-bold">MoboEat</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your trusted partner for exceptional corporate canteen services across India.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#home" className="text-gray-400 hover:text-orange-400 transition">Home</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-orange-400 transition">Services</a></li>
                <li><a href="#menu" className="text-gray-400 hover:text-orange-400 transition">Menu</a></li>
                <li><a href="#reviews" className="text-gray-400 hover:text-orange-400 transition">Reviews</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition">Corporate Catering</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition">Bulk Orders</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition">Event Catering</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition">Custom Menus</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect With Us</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2">
                  <Phone size={18} className="text-orange-400" />
                  <a href="tel:+918767381818" className="text-gray-400 hover:text-orange-400 transition">+91 876 738 1818</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail size={18} className="text-orange-400" />
                  <a href="mailto:info@moboeat.com" className="text-gray-400 hover:text-orange-400 transition">info@moboeat.com</a>
                </li>
                <li className="flex items-center space-x-2">
                  <MapPin size={18} className="text-orange-400" />
                  <span className="text-gray-400">Mumbai, India</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400 mb-4">
              © 2026 MoboEat. All rights reserved. | FSSAI Licensed | ISO 22000 Certified
            </p>
            <div className="flex justify-center space-x-6">
              <Link to={DELETE_ACCOUNT_ROUTE}>Delete Account</Link>
              <span className="text-gray-600">|</span>
              <Link to={PRIVACY_POLICY_ROUTE} className="text-gray-400 hover:text-orange-400 transition">Privacy Policy</Link>
              <span className="text-gray-600">|</span>
              <Link to={TERMS_CONDITION_ROUTE} className="text-gray-400 hover:text-orange-400 transition">Terms of Service</Link>
              <span className="text-gray-600">|</span>
              <Link to={REFUND_POLICY_ROUTE} className="text-gray-400 hover:text-orange-400 transition">Refund Policy</Link>
              <span className="text-gray-600">|</span>

              <Link to={CONTACT_ROUTE}>Contact Us</Link>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MoboEatHomePage;